import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { SENTIMENT_META, type CustomerSentiment } from "@/lib/customerInsights";

export const SentimentBadge = ({ sentiment }: { sentiment: CustomerSentiment }) => (
  <span
    className={cn(
      "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
      SENTIMENT_META[sentiment].badge,
    )}
  >
    {SENTIMENT_META[sentiment].label}
  </span>
);

// Values already written in during this visit. Sorting, filtering or coming
// back from a profile re-mounts rows; those values should just be there.
const writtenIn = new Set<string>();

// Past the first screen or two nobody sees the reveal, and thousands of
// running animations would cost more than they show.
const MAX_ANIMATED_ROWS = 30;

interface CustomerInsightValueProps {
  /** Identifies this exact value; a new value writes itself in again. */
  revealKey: string;
  rowIndex: number;
  columnIndex: number;
  title?: string;
  children: ReactNode;
}

/**
 * An inferred table value that appears the way the landing page's
 * "Writes its own notes" table does: a shimmering placeholder, then the value
 * fading in with a brief brand tint, staggered across rows and columns.
 */
export function CustomerInsightValue(props: CustomerInsightValueProps) {
  return <Reveal key={props.revealKey} {...props} />;
}

function Reveal({ revealKey, rowIndex, columnIndex, title, children }: CustomerInsightValueProps) {
  const [animating, setAnimating] = useState(
    () => rowIndex < MAX_ANIMATED_ROWS && !writtenIn.has(revealKey),
  );
  useEffect(() => {
    writtenIn.add(revealKey);
  }, [revealKey]);

  if (!animating) {
    return <span className="block truncate" title={title}>{children}</span>;
  }

  const delay = 0.15 + Math.min(rowIndex, 15) * 0.08 + columnIndex * 0.1;
  return (
    <span className="customer-insight-reveal" style={{ "--insight-delay": `${delay}s` } as CSSProperties}>
      <i aria-hidden className="customer-insight-skeleton" />
      <span
        className="customer-insight-text block truncate"
        title={title}
        onAnimationEnd={() => setAnimating(false)}
      >
        {children}
      </span>
    </span>
  );
}
